import { cn, Select, SelectContent, SelectItem, SelectTrigger } from '@/components/ui';
import { useMemo } from 'react';
import { Globe } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { updateMyLanguage } from '@/lib/api';
import { useAuthStore } from '@/state/authState';

interface LanguageSwitcherProps {
  className?: string;
  // landing: anasayfa (Roadmap v9) menüsü için çerçeveli, dünya ikonlu kompakt görünüm
  // auth: giriş/kayıt sayfaları (Giriş tasarımı) için yalnızca dil kodunu gösteren kare düğme
  variant?: 'default' | 'header' | 'landing' | 'auth';
}
const languageOptions = [
  { value: 'en', labelKey: 'language.english', code: 'EN' },
  { value: 'tr', labelKey: 'language.turkish', code: 'TR' },
  { value: 'de', labelKey: 'language.german', code: 'DE' },
  { value: 'el', labelKey: 'language.greek', code: 'EL' },
  { value: 'fr', labelKey: 'language.french', code: 'FR' },
];
export const LanguageSwitcher = ({ className, variant = 'default' }: LanguageSwitcherProps) => {
  const { i18n, t } = useTranslation();
  const currentLang = (i18n.resolvedLanguage || i18n.language || 'en').split('-')[0] as
    | 'en'
    | 'tr'
    | 'de'
    | 'el'
    | 'fr';
  const currentOption = useMemo(() => {
    return languageOptions.find((option) => option.value === currentLang) ?? languageOptions[0];
  }, [currentLang]);
  const showLabel = variant === 'default';

  const handleLanguageChange = (value: string) => {
    i18n.changeLanguage(value);

    // Persist the choice for signed-in users so it survives across sessions/devices and drives
    // transactional email language. On public pages (landing/KVKK) there is no user — the switch
    // stays local. Fire-and-forget: the UI has already changed and a save failure is non-blocking.
    const { user, setUser } = useAuthStore.getState();
    if (user && user.language !== value) {
      setUser({ ...user, language: value });
      void updateMyLanguage(value).catch(() => {
        /* silent — persisted preference is best-effort, UI language already applied */
      });
    }
  };

  return (
    <Select value={currentLang} onValueChange={handleLanguageChange}>
      <SelectTrigger
        className={cn(
          'w-[160px] bg-card/60 border-border text-sm font-medium [&>svg]:hidden',
          variant === 'header' &&
            'h-auto w-auto p-0 border-none bg-transparent hover:bg-transparent justify-center rounded-none shadow-none focus-visible:ring-0 focus-visible:ring-offset-0 min-w-0 mx-2',
          variant === 'landing' &&
            'h-[38px] w-auto min-w-0 gap-1.5 rounded-[10px] border border-[var(--public-line-strong)] bg-transparent px-3 font-[Geist,ui-sans-serif,system-ui,sans-serif] text-[14px] text-public-ink shadow-none hover:bg-[var(--public-line-faint)] [&>svg]:block [&>svg]:h-3.5 [&>svg]:w-3.5 [&>svg]:opacity-60',
          variant === 'auth' &&
            'h-10 w-11 min-w-0 justify-center rounded-[12px] border border-[var(--public-line)] bg-public-paper p-0 font-[Geist,ui-sans-serif,system-ui,sans-serif] text-[13px] text-public-ink shadow-none hover:bg-[var(--public-line-faint)]',
          className
        )}
        style={variant === 'header' ? { borderRadius: 0 } : undefined}
        aria-label={t('language.label')}
      >
        <div className={cn('flex items-center gap-2', !showLabel && 'gap-0', variant === 'landing' && 'gap-1.5')}>
          {variant === 'landing' && <Globe className='h-4 w-4' aria-hidden='true' />}
          <span className={cn('font-semibold cursor-pointer', variant === 'header' && 'text-sm')}>
            {currentOption.code}
          </span>
          {showLabel && <span>{t(currentOption.labelKey)}</span>}
        </div>
      </SelectTrigger>
      <SelectContent align='end'>
        {languageOptions.map((option) => (
          <SelectItem key={option.value} value={option.value}>
            <div className='flex items-center gap-2'>
              <span className='font-semibold'>{option.code}</span>
              <span>{t(option.labelKey)}</span>
            </div>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
};
